// ProjectReview — the first-class "Review" pipeline stage (D2 stage 5: "invite
// reviewers, collect decisions"). Previously the pipeline Review dot routed to
// the "Next Steps" tab and there was no dedicated Review destination (test S2-05).
//
// This is the project-scoped review hub: the owner picks one of THIS project's
// documents and one or more of its collaborators (real user ids the backend's
// _reviewer_allowed guard accepts — not free-text emails), sends parallel or
// sequential review requests, and sees every reviewer's decision + feedback in
// one place. A reviewer looking at their own project can act on a pending
// request inline (approve / request changes / reject) without leaving for the
// personal Tasks & Reviews queue.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardCheck, Plus, X, CheckCircle2, XCircle, Clock, AlertCircle, Ban, MessageSquare,
} from 'lucide-react'
import { getReviews, createReview, cancelReview, submitReviewDecision } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import type { Review, Document, ProjectOwner, Permission } from '../../types'
import EmptyState from '../ui/EmptyState'
import LoadingSkeleton from '../shared/LoadingSkeleton'

interface ProjectReviewProps {
  projectId: string
  documents: Document[]
  owners: ProjectOwner[]
  collaborators: Permission[]
}

const STATUS_STYLE: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  pending: { label: 'Awaiting decision', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock size={12} /> },
  queued: { label: 'Queued', cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: <AlertCircle size={12} /> },
  completed: { label: 'Decision in', cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircle2 size={12} /> },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-50 text-gray-500 border-gray-200', icon: <Ban size={12} /> },
}

const DECISION_STYLE: Record<string, { label: string; cls: string }> = {
  approved: { label: 'Approved', cls: 'text-green-700' },
  rejected: { label: 'Rejected', cls: 'text-red-700' },
  changes_requested: { label: 'Changes requested', cls: 'text-amber-700' },
}

export default function ProjectReview({ projectId, documents, owners, collaborators }: ProjectReviewProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteDoc, setInviteDoc] = useState('')
  const [inviteReviewers, setInviteReviewers] = useState<Set<string>>(new Set())
  const [reviewType, setReviewType] = useState<'parallel' | 'sequential'>('parallel')
  const [message, setMessage] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [feedbackFor, setFeedbackFor] = useState<string | null>(null)
  const [feedbackText, setFeedbackText] = useState('')

  const { data: reviews, isLoading, isError, refetch } = useQuery<Review[]>({
    queryKey: ['reviews'],
    queryFn: () => getReviews(),
  })

  const docIds = useMemo(() => new Set(documents.map((d) => d.document_id)), [documents])
  const projectReviews = useMemo(
    () => (reviews ?? []).filter((r) => docIds.has(r.document_id)),
    [reviews, docIds],
  )

  const docTitle = (id: string) => documents.find((d) => d.document_id === id)?.title ?? `Document ${id.slice(0, 8)}`

  // Valid reviewer targets = the project's owners + user collaborators (by user
  // id), minus the caller. These are exactly who the backend's _reviewer_allowed
  // guard accepts, so an invite never bounces with a 400.
  //
  // Bug #6275: a legacy grant-creation path could leave TWO permission rows for
  // the same real person — one correctly keyed by their Cognito sub, one by a
  // raw email or a group:/extorg: id. Deduping by raw grantee_id let both
  // through as separate, visually identical checkboxes. Merge by the resolved
  // identity (email, falling back to display name) instead, and when two rows
  // resolve to the same person, keep the one whose id is a plain user id (not
  // email-shaped, not group:/extorg:-prefixed) — that is the id the backend's
  // guard will actually accept.
  const reviewerOptions = useMemo(() => {
    const isMalformedShape = (id: string) =>
      id.includes('@') || id.startsWith('group:') || id.startsWith('extorg:')
    const byIdentity = new Map<string, { id: string; label: string }>()
    const upsert = (id: string, label: string) => {
      const key = label || id
      const existing = byIdentity.get(key)
      if (!existing || (isMalformedShape(existing.id) && !isMalformedShape(id))) {
        byIdentity.set(key, { id, label })
      }
    }
    owners.forEach((o) => upsert(o.user_id, o.email))
    collaborators
      .filter((c) => (c.grantee_type ?? 'user') === 'user' && c.grantee_id)
      .forEach((c) => upsert(c.grantee_id, c.grantee_email || c.grantee_display_name || c.grantee_id))
    const options = [...byIdentity.values()]
    return user?.id ? options.filter((o) => o.id !== user.id) : options
  }, [owners, collaborators, user?.id])

  const reviewerLabel = (id: string) => {
    const owner = owners.find((o) => o.user_id === id)
    if (owner) return owner.email
    const collab = collaborators.find((c) => c.grantee_id === id)
    return collab?.grantee_email || collab?.grantee_display_name || `User ${id.slice(0, 8)}`
  }

  const summary = useMemo(() => {
    const s = { pending: 0, approved: 0, rejected: 0, changes: 0 }
    for (const r of projectReviews) {
      if (r.status === 'pending' || r.status === 'queued') s.pending += 1
      else if (r.decision === 'approved') s.approved += 1
      else if (r.decision === 'rejected') s.rejected += 1
      else if (r.decision === 'changes_requested') s.changes += 1
    }
    return s
  }, [projectReviews])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['reviews'] })

  const createMutation = useMutation({
    mutationFn: () =>
      createReview({
        document_id: inviteDoc,
        reviewers: [...inviteReviewers],
        review_type: reviewType,
        message: message.trim() || undefined,
      }),
    onSuccess: () => {
      invalidate()
      setShowInvite(false)
      setInviteDoc('')
      setInviteReviewers(new Set())
      setMessage('')
      setInviteError('')
    },
    onError: (e: unknown) =>
      setInviteError(e instanceof Error ? e.message : 'Could not send the review request. Please try again.'),
  })

  const cancelMutation = useMutation({
    mutationFn: (reviewId: string) => cancelReview(reviewId),
    onSuccess: invalidate,
  })

  const decisionMutation = useMutation({
    mutationFn: ({ reviewId, decision, feedback }: { reviewId: string; decision: string; feedback?: string }) =>
      submitReviewDecision(reviewId, { decision, feedback }),
    onSuccess: () => {
      invalidate()
      setFeedbackFor(null)
      setFeedbackText('')
    },
  })

  const toggleReviewer = (id: string) =>
    setInviteReviewers((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const inviteValid = inviteDoc && inviteReviewers.size > 0 && !createMutation.isPending

  return (
    <div className="space-y-5" data-project-id={projectId}>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-iflow-50 text-iflow-700 flex items-center justify-center flex-shrink-0">
            <ClipboardCheck size={20} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Review</h3>
            <p className="text-sm text-gray-500">
              Invite reviewers to assess this project's documents and collect their decisions in one place.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setShowInvite((v) => !v); setInviteError('') }}
          disabled={documents.length === 0}
          className="inline-flex items-center gap-1.5 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          title={documents.length === 0 ? 'Add a document to this project before requesting a review' : undefined}
        >
          <Plus size={16} /> Invite reviewer
        </button>
      </div>

      {/* Summary */}
      {projectReviews.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryTile label="Awaiting" value={summary.pending} cls="text-amber-700" />
          <SummaryTile label="Approved" value={summary.approved} cls="text-green-700" />
          <SummaryTile label="Changes" value={summary.changes} cls="text-amber-700" />
          <SummaryTile label="Rejected" value={summary.rejected} cls="text-red-700" />
        </div>
      )}

      {/* Invite panel */}
      {showInvite && (
        <div className="light-card p-4 lg:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-gray-800">Request a review</div>
            <button type="button" onClick={() => setShowInvite(false)} className="p-1 text-gray-500 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>

          {inviteError && (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{inviteError}</div>
          )}

          <label className="block text-xs font-semibold text-gray-600 mb-1">Document</label>
          <select
            value={inviteDoc}
            onChange={(e) => setInviteDoc(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white mb-3"
          >
            <option value="">Select a document…</option>
            {documents.map((d) => (
              <option key={d.document_id} value={d.document_id}>{d.title || d.filename || d.document_id}</option>
            ))}
          </select>

          <label className="block text-xs font-semibold text-gray-600 mb-1">Reviewers</label>
          {reviewerOptions.length === 0 ? (
            <p className="text-sm text-gray-500 mb-3">
              No collaborators to invite yet — grant someone access to this project first (Access tab), then request their review.
            </p>
          ) : (
            <ul className="mb-3 space-y-1 max-h-44 overflow-y-auto">
              {reviewerOptions.map((r) => (
                <li key={r.id}>
                  <label className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inviteReviewers.has(r.id)}
                      onChange={() => toggleReviewer(r.id)}
                      className="w-4 h-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500"
                    />
                    <span className="text-sm text-gray-800">{r.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}

          <label className="block text-xs font-semibold text-gray-600 mb-1">Order</label>
          <div className="flex gap-2 mb-3">
            {(['parallel', 'sequential'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setReviewType(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border capitalize ${
                  reviewType === t ? 'bg-iflow-50 border-iflow-400 text-iflow-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t}
              </button>
            ))}
            <span className="text-xs text-gray-400 self-center">
              {reviewType === 'sequential' ? 'each reviewer acts in turn' : 'all reviewers act at once'}
            </span>
          </div>

          <label className="block text-xs font-semibold text-gray-600 mb-1">Message (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            placeholder="What would you like them to focus on?"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-4"
          />

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowInvite(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createMutation.mutate()}
              disabled={!inviteValid}
              className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Sending…' : `Send ${inviteReviewers.size || ''} request${inviteReviewers.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {isLoading ? (
        <LoadingSkeleton rows={3} type="list" />
      ) : isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>Could not load reviews.</span>
          <button type="button" onClick={() => refetch()} className="underline font-medium">Retry</button>
        </div>
      ) : projectReviews.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No reviews yet"
          description="Invite a collaborator to review one of this project's documents — their decision and feedback will appear here."
        />
      ) : (
        <div className="space-y-2">
          {projectReviews.map((r) => {
            const st = STATUS_STYLE[r.status] ?? STATUS_STYLE.pending
            const dec = r.decision ? DECISION_STYLE[r.decision] : null
            const iAmRequester = user?.id === r.requester_id
            const iAmReviewer = user?.id === r.reviewer_id
            const actionable = iAmReviewer && r.status === 'pending'
            const cancellable = iAmRequester && (r.status === 'pending' || r.status === 'queued')
            return (
              <div key={r.review_id} className="light-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{docTitle(r.document_id)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Reviewer: <span className="text-gray-700">{reviewerLabel(r.reviewer_id)}</span>
                      {' · '}{r.review_type === 'sequential' ? `sequential (step ${r.sequence_order + 1})` : 'parallel'}
                    </p>
                    {r.message && <p className="text-xs text-gray-500 mt-1 italic">“{r.message}”</p>}
                    {r.feedback && (
                      <p className="text-xs text-gray-600 mt-1 flex items-start gap-1">
                        <MessageSquare size={12} className="mt-0.5 flex-shrink-0 text-gray-400" /> {r.feedback}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${st.cls}`}>
                      {st.icon} {st.label}
                    </span>
                    {dec && <span className={`text-[11px] font-semibold ${dec.cls}`}>{dec.label}</span>}
                  </div>
                </div>

                {(actionable || cancellable) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
                    {actionable && feedbackFor !== r.review_id && (
                      <>
                        <button
                          type="button"
                          onClick={() => decisionMutation.mutate({ reviewId: r.review_id, decision: 'approved' })}
                          disabled={decisionMutation.isPending}
                          className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          <CheckCircle2 size={13} /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => { setFeedbackFor(r.review_id); setFeedbackText('') }}
                          className="inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50"
                        >
                          <MessageSquare size={13} /> Request changes / reject
                        </button>
                      </>
                    )}
                    {actionable && feedbackFor === r.review_id && (
                      <div className="w-full space-y-2">
                        <textarea
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          rows={2}
                          placeholder="Add feedback for the requester…"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => decisionMutation.mutate({ reviewId: r.review_id, decision: 'changes_requested', feedback: feedbackText.trim() || undefined })}
                            disabled={decisionMutation.isPending}
                            className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                          >
                            Request changes
                          </button>
                          <button
                            type="button"
                            onClick={() => decisionMutation.mutate({ reviewId: r.review_id, decision: 'rejected', feedback: feedbackText.trim() || undefined })}
                            disabled={decisionMutation.isPending}
                            className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                          >
                            <XCircle size={13} /> Reject
                          </button>
                          <button type="button" onClick={() => setFeedbackFor(null)} className="text-xs text-gray-500 hover:text-gray-700 px-2">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {cancellable && (
                      <button
                        type="button"
                        onClick={() => cancelMutation.mutate(r.review_id)}
                        disabled={cancelMutation.isPending}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 ml-auto"
                      >
                        <Ban size={13} /> Withdraw request
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SummaryTile({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="light-card px-4 py-3 text-center">
      <div className={`text-xl font-bold ${cls}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}
